import type { FundsReservationState, WithdrawalConfirmationsDto, WithdrawalState, WithdrawalStatus } from '@shared/api/contracts';

export const WITHDRAWAL_STATES: WithdrawalState[] = [
  'created',
  'aml_pending',
  'approval_pending',
  'approved',
  'liquidity_provider_process',
  'signing',
  'broadcasted',
  'confirming',
  'completed',
  'manual_review',
  'info_requested',
  'blocked',
  'rejected',
  'failed',
  'cancelled',
];

const STATE_SET = new Set<string>(WITHDRAWAL_STATES);

export type MerchantStage =
  | 'review'
  | 'processing'
  | 'completed'
  | 'info'
  | 'paused'
  | 'rejected'
  | 'failed'
  | 'cancelled'
  | 'unknown';

const PRE_CLAIM: WithdrawalState[] = ['created', 'aml_pending', 'approval_pending', 'approved', 'blocked'];

const TERMINAL: WithdrawalState[] = ['completed', 'rejected', 'failed', 'cancelled'];

const RECONCILE_REASON = /UNKNOWN|RECONCILE|BROADCAST|HASH|RPC|MISMATCH/i;

export const isCanonicalWithdrawalState = (value: string | null | undefined): value is WithdrawalState =>
  Boolean(value && STATE_SET.has(value));

export const isTerminalWithdrawalState = (state: string | null | undefined): boolean =>
  isCanonicalWithdrawalState(state) && TERMINAL.includes(state);

export const isActiveWithdrawalState = (state: string | null | undefined, status?: string | null): boolean => {
  if (isCanonicalWithdrawalState(state)) return !isTerminalWithdrawalState(state);
  return status === 'pending'
    || status === 'processing'
    || status === 'aml_pending'
    || status === 'manual_review'
    || status === 'info_requested'
    || status === 'liquidity_provider_process';
};

export const merchantStage = (state: string | null | undefined): MerchantStage => {
  switch (state) {
    case 'created':
    case 'aml_pending':
    case 'approval_pending':
    case 'manual_review':
      return 'review';
    case 'approved':
    case 'liquidity_provider_process':
    case 'signing':
    case 'broadcasted':
    case 'confirming':
      return 'processing';
    case 'completed':
      return 'completed';
    case 'info_requested':
      return 'info';
    case 'blocked':
      return 'paused';
    case 'rejected':
      return 'rejected';
    case 'failed':
      return 'failed';
    case 'cancelled':
      return 'cancelled';
    default:
      return 'unknown';
  }
};

const legacyStage = (status: string | null | undefined): MerchantStage => {
  switch (status) {
    case 'paid':
    case 'success':
      return 'completed';
    case 'cancel_by_user':
      return 'cancelled';
    case 'cancel_by_admin':
      return 'rejected';
    case 'error':
      return 'failed';
    case 'info_requested':
      return 'info';
    case 'aml_pending':
    case 'manual_review':
    case 'pending':
      return 'review';
    case 'processing':
    case 'liquidity_provider_process':
      return 'processing';
    default:
      return 'unknown';
  }
};

export const merchantStageOf = (state: string | null | undefined, status: string | null | undefined): MerchantStage => {
  if (state) return merchantStage(state);
  return legacyStage(status);
};

export const merchantStageLabelKey = (state: string | null | undefined, status: string | null | undefined): string => {
  const stage = merchantStageOf(state, status);
  return stage === 'unknown' ? 'withdrawals.status.unknown' : `withdrawals.stage.${stage}`;
};

export const adminStateLabelKey = (state: string | null | undefined, status: string | null | undefined): string => {
  if (state && !isCanonicalWithdrawalState(state)) return 'withdrawals.status.unknown';
  if (isCanonicalWithdrawalState(state)) return `withdrawals.adminState.${state}`;
  if (status === 'paid' || status === 'success') return 'withdrawals.stage.completed';
  if (status === 'cancel_by_user') return 'withdrawals.adminState.cancelled';
  if (status === 'cancel_by_admin') return 'withdrawals.adminState.rejected';
  if (status === 'error') return 'withdrawals.adminState.failed';
  if (status === 'aml_pending' || status === 'manual_review' || status === 'info_requested' || status === 'liquidity_provider_process') {
    return `withdrawals.status.${status}`;
  }
  return 'withdrawals.status.unknown';
};

export const merchantHintKey = (state: string | null | undefined, reasonCode?: string | null): string => {
  if (state === 'manual_review') {
    if (reasonCode && RECONCILE_REASON.test(reasonCode)) return 'withdrawals.hint.reconcile';
    if (!reasonCode) return 'withdrawals.hint.manualReviewSafe';
    return 'withdrawals.hint.manual_review';
  }
  if (isCanonicalWithdrawalState(state)) return `withdrawals.hint.${state}`;
  return 'withdrawals.hint.unknown';
};

export const fundsLabelKey = (funds: FundsReservationState | null | undefined): string | null => {
  if (funds === 'RESERVED' || funds === 'COMMITTED' || funds === 'RELEASED') return `withdrawals.funds.${funds}`;
  return null;
};

export const isMerchantCancellableState = (state: string | null | undefined, status: WithdrawalStatus | string): boolean => {
  if (isCanonicalWithdrawalState(state)) return PRE_CLAIM.includes(state);
  return status === 'pending' || status === 'aml_pending';
};

export const awaitsAdminApproval = (state: string | null | undefined, status: string): boolean =>
  state ? state === 'approval_pending' : status === 'pending';

export interface ConfirmationView {
  kind: 'count' | 'finality' | 'none';
  current?: number;
  required?: number;
}

export const confirmationView = (confirmations: WithdrawalConfirmationsDto | null | undefined): ConfirmationView => {
  if (!confirmations) return { kind: 'none' };
  if (typeof confirmations.required === 'number') {
    return {
      kind: 'count',
      current: typeof confirmations.current === 'number' ? confirmations.current : 0,
      required: confirmations.required,
    };
  }
  if (confirmations.mode || confirmations.finalized === false) return { kind: 'finality' };
  return { kind: 'none' };
};

export interface MerchantHistoryStep {
  state: string;
  at: string;
  reasonCode: string | null;
}

export const mapStateEventList = (payload: unknown): MerchantHistoryStep[] => {
  const rows = Array.isArray(payload)
    ? payload
    : payload && typeof payload === 'object' && Array.isArray((payload as { data?: unknown }).data)
      ? (payload as { data: unknown[] }).data
      : [];

  return rows.flatMap((row) => {
    if (!row || typeof row !== 'object') return [];
    const record = row as {
      to?: string | null;
      toState?: string | null;
      state?: string | null;
      occurredAt?: string | null;
      createdAt?: string | null;
      reasonCode?: string | null;
    };
    const state = record.to ?? record.toState ?? record.state;
    const at = record.occurredAt ?? record.createdAt;
    if (!state || !at) return [];
    return [{ state, at, reasonCode: record.reasonCode ?? null }];
  });
};

/** Adjacent canonical stages that share the merchant "processing" stage collapse into the latest one. */
export const collapseMerchantHistory = (steps: MerchantHistoryStep[]): MerchantHistoryStep[] => {
  const collapsed: MerchantHistoryStep[] = [];
  steps.forEach((step) => {
    const previous = collapsed[collapsed.length - 1];
    const sameProcessing = previous
      && merchantStage(previous.state) === 'processing'
      && merchantStage(step.state) === 'processing';
    if (sameProcessing) {
      collapsed[collapsed.length - 1] = step;
      return;
    }
    collapsed.push(step);
  });
  return collapsed;
};

export const withdrawalExplorerUrl = (network: string, txid: string): string | null => {
  const code = network.trim().toUpperCase().replace(/-/g, '');
  if (code === 'TRC20') return `https://tronscan.org/#/transaction/${encodeURIComponent(txid)}`;
  if (code === 'ERC20') return `https://etherscan.io/tx/${encodeURIComponent(txid)}`;
  if (code === 'BEP20') return `https://bscscan.com/tx/${encodeURIComponent(txid)}`;
  if (code === 'BTC') return `https://mempool.space/tx/${encodeURIComponent(txid)}`;
  if (code === 'LTC') return `https://blockchair.com/litecoin/transaction/${encodeURIComponent(txid)}`;
  return null;
};
